import React, { PropsWithChildren } from 'react';

import { useTranslation } from 'react-i18next';

import { Tooltip } from '@/components/floating/Tooltip.tsx';
import {
  PumpEvent,
  getPumpEventTranslation,
} from '@/models/InsulinPumpEvents.ts';
import { Insulin } from '@/models/diabetes/InsulinModel.ts';
import { SvgDataListTooltip } from '@/uiKit/atoms/svg/SvgDataListTooltip';
import {
  SvgDataTooltip,
  SvgDataTooltipFooter,
  SvgDataTooltipKeyValueContent,
  SvgDataTooltipSubtitle,
  SvgDataTooltipTitle,
} from '@/uiKit/atoms/svg/SvgDataTooltip.tsx';
import { formatNumber } from '@/utils/mathUtils.ts';

const PumpBolusContent: React.FC<{ insulin: Insulin }> = ({ insulin }) => {
  const { t } = useTranslation();
  let carbs = null;
  let correction = null;
  if (
    insulin.bolusSettings?.type === 'carbs' ||
    insulin.bolusSettings?.type === 'carbs_and_correction'
  ) {
    carbs = (
      <>
        {insulin.bolusSettings.carbs ? (
          <SvgDataTooltipKeyValueContent
            label={t('insulin.carbs')}
            value={`${formatNumber(insulin.bolusSettings.carbs, 1)} g`}
            emphasis="none"
          />
        ) : null}
        {insulin.bolusSettings.carbsRatio ? (
          <SvgDataTooltipKeyValueContent
            label={t('insulin.carbsRatio')}
            value={`${formatNumber(insulin.bolusSettings.carbsRatio, 1)} g/U`}
            emphasis="none"
          />
        ) : null}
        <SvgDataTooltipKeyValueContent
          label={t('insulin.foodInsulin')}
          value={`${formatNumber(insulin.bolusSettings.foodInsulin, 1)} U`}
          emphasis="none"
        />
      </>
    );
  }
  if (
    insulin.bolusSettings?.type === 'correction' ||
    insulin.bolusSettings?.type === 'carbs_and_correction'
  ) {
    correction = (
      <>
        {insulin.bolusSettings.glycemia ? (
          <SvgDataTooltipKeyValueContent
            label={t('insulin.glycemia')}
            value={`${formatNumber(insulin.bolusSettings.glycemia, 0)} mg/dL`}
            emphasis="none"
          />
        ) : null}
        {insulin.bolusSettings.insulinSensitivity ? (
          <SvgDataTooltipKeyValueContent
            label={t('insulin.insulinSensitivity')}
            value={`${formatNumber(insulin.bolusSettings.insulinSensitivity, 1)} mg/dL/U`}
            emphasis="none"
          />
        ) : null}
        {insulin.bolusSettings.targetGlycemia ? (
          <SvgDataTooltipKeyValueContent
            label={t('insulin.targetGlycemia')}
            value={`${formatNumber(insulin.bolusSettings.targetGlycemia, 0)} mg/dL`}
            emphasis="none"
          />
        ) : null}
        <SvgDataTooltipKeyValueContent
          label={t('insulin.correction')}
          value={`${formatNumber(insulin.bolusSettings.correctionInsulin, 1)} U`}
          emphasis="none"
        />
      </>
    );
  }

  return (
    <>
      {correction}
      {insulin.bolusSettings?.iob ? (
        <SvgDataTooltipKeyValueContent
          label={t('insulin.iob')}
          value={`${formatNumber(insulin.bolusSettings.iob, 1)} U`}
          emphasis="none"
        />
      ) : null}
      {carbs}
      <SvgDataTooltipKeyValueContent
        label={t('insulin.quantity')}
        value={`${formatNumber(insulin.quantity, 1)} U`}
        emphasis="all"
      />
    </>
  );
};

const InsulinTooltipContent: React.FC<{ item: Insulin }> = ({ item }) => {
  const { t } = useTranslation();
  switch (true) {
    case item.reason === 'basal' && item.method === 'pen':
      return (
        <SvgDataTooltip>
          <SvgDataTooltipTitle title={`${formatNumber(item.quantity, 1)} U`} />
          <SvgDataTooltipSubtitle subtitle={t('insulin.title')} />
          <SvgDataTooltipFooter
            date={item.date}
            inputMethod={item.deviceType}
          />
        </SvgDataTooltip>
      );
    case item.reason === 'bolus' && item.method === 'pen':
      return (
        <SvgDataTooltip>
          <SvgDataTooltipTitle title={`${formatNumber(item.quantity, 1)} U`} />
          <SvgDataTooltipSubtitle subtitle={t('insulin.title')} />
          <SvgDataTooltipFooter
            date={item.date}
            inputMethod={item.deviceType}
          />
        </SvgDataTooltip>
      );
    case item.reason === 'basal' && item.method === 'pump':
      return (
        <SvgDataTooltip>
          <SvgDataTooltipTitle
            title={`${formatNumber(item.quantity, 1)} U/h`}
          />
          <SvgDataTooltipSubtitle subtitle={t('insulin.title')} />
          <SvgDataTooltipFooter
            date={item.date}
            inputMethod={item.deviceType}
          />
        </SvgDataTooltip>
      );

    case item.reason === 'bolus':
      return (
        <SvgDataTooltip>
          <PumpBolusContent insulin={item} />
          <SvgDataTooltipFooter
            date={item.date}
            inputMethod={item.deviceType}
          />
        </SvgDataTooltip>
      );
    default:
      return null;
  }
};

export type InsulinGraphTooltipProps = PropsWithChildren<{
  insulin: Insulin;
}>;

export const InsulinGraphTooltip: React.FC<InsulinGraphTooltipProps> = ({
  insulin,
  children,
}) => (
  <Tooltip
    svg
    placement="top"
    dismiss={false}
    autoOpen
    offset={0}
    content={
      <SvgDataListTooltip>
        {[insulin].map(item => (
          <InsulinTooltipContent key={item.id} item={item} />
        ))}
      </SvgDataListTooltip>
    }
  >
    {children}
  </Tooltip>
);

export type InsulinPointTooltipProps = PropsWithChildren<{
  insulins: Insulin[];
  x?: number;
  y?: number;
}>;

export const InsulinPointTooltip: React.FC<InsulinPointTooltipProps> = ({
  insulins,
  x,
  y,
  children,
}) => (
  <Tooltip
    svg
    placement="top"
    relativeClientPoint
    clientPoint={{
      enabled: true,
      x: x ?? 0,
      y: y ?? 0,
    }}
    content={
      <SvgDataListTooltip>
        {insulins.map(item => (
          <InsulinTooltipContent key={item.id} item={item} />
        ))}
      </SvgDataListTooltip>
    }
  >
    {children}
  </Tooltip>
);

const PumpEventTooltipContent: React.FC<{ item: PumpEvent }> = ({ item }) => {
  const { t } = useTranslation();

  switch (item.category) {
    case 'cartridge_filled':
      return item.insulinActual ? (
        <SvgDataTooltipKeyValueContent
          label={t('pumpEvents.insulin')}
          value={`${formatNumber(item.insulinActual, 1)} U`}
        />
      ) : null;

    case 'cannula_filled':
    case 'tubing_filled':
      return item.primeSize ? (
        <SvgDataTooltipKeyValueContent
          label={t('pumpEvents.primeSize')}
          value={`${formatNumber(item.primeSize, 1)} U`}
        />
      ) : null;
    default:
      return null;
  }
};

export type PumpEventTooltipProps = PropsWithChildren<{
  events: PumpEvent[];
  x?: number;
  y?: number;
}>;

export const PumpEventTooltip: React.FC<PumpEventTooltipProps> = ({
  events,
  x,
  y,
  children,
}) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      svg
      placement="top"
      relativeClientPoint
      clientPoint={{
        enabled: true,
        x: x ?? 0,
        y: y ?? 0,
      }}
      content={
        <SvgDataListTooltip>
          {events.map(item => (
            <SvgDataTooltip key={item.id}>
              <SvgDataTooltipKeyValueContent
                label={t('pumpEvents.category')}
                value={t(getPumpEventTranslation(item.category))}
              />
              <PumpEventTooltipContent item={item} />
              <SvgDataTooltipFooter date={item.date} />
            </SvgDataTooltip>
          ))}
        </SvgDataListTooltip>
      }
    >
      {children}
    </Tooltip>
  );
};
